import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { History } from 'history';
import { useTranslation } from 'react-i18next';

import { Button } from 'theSource/components/Button';
import { Dialog } from 'theSource/components/Dialog';
import { useWindowGuard } from 'theSource/utils';
import { Spacer } from 'theSource/components/Spacer';

interface DirtyFormPromptProps {
  isDirty?: boolean;
}

export const DirtyFormPrompt = ({ isDirty = false }: DirtyFormPromptProps) => {
  const { t } = useTranslation();
  const [isFlagged, setIsFlagged] = useState(false);
  const confirm = useRef<any>(null);
  const context = useRef<any>(null);
  const { navigator } = useContext(NavigationContext);
  const blockNavigator = navigator as History;

  useWindowGuard(isDirty);

  const next = useCallback(() => {
    setIsFlagged(false);
    confirm.current?.();

    // This setTimeout solves PWA-460 issue in iOS devices
    setTimeout(() => {
      context.current?.retry?.();
    });
  }, []);

  useEffect(() => {
    if (!isDirty) {
      next();
      return;
    }

    const unblock = blockNavigator.block((tx) => {
      setIsFlagged(true);
      context.current = tx;
    });

    confirm.current = unblock;

    return unblock;
  }, [blockNavigator, isDirty, next]);

  const handleCancel = () => {
    setIsFlagged(false);
  };

  const handleConfirm = () => {
    next();
  };

  if (!isFlagged) {
    return null;
  }

  return (
    <Dialog
      title={t('messages.dialog:unsavedDataModalTitle')}
      message={t('messages.dialog:unsavedDataModalMessage')}
      isOpen={isFlagged}
      showWarningIcon
    >
      <Button
        variant="tertiary"
        onClick={handleCancel}
        label={t('common:cancel')}
      />
      <Spacer width={6} />
      <Button
        variant="primary"
        onClick={handleConfirm}
        label={t('common:confirm')}
      />
    </Dialog>
  );
};
